import api from '.'

/**
 * API class for handling billing profiles related to an admin organization.
 *
 * This class provides methods to interact with the backend services to manage billing profiles, such as retrieving,
 * updating, and deleting billing information for an admin organization.
 */
class AdminOrganizationBillingProfileApi {
    /**
     * Retrieves a list of billing profiles for a specified organization by making an API request.
     *
     * @param {string} organization_id - The unique identifier of the organization.
     * @returns {Promise<Object>} A promise that resolves to the response data containing the list of billing profiles.
     */
    list(organization_id) {
        const payload = {
            organization_id
        }

        return api
            .sentRequest(
                '/api/organization/admin/billing/profile/list',
                payload,
                true
            )
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Fetches billing profile information for a specific organization.
     *
     * @param {string} organization_id - The ID of the organization.
     * @param {string} organization_billing_profile_id - The ID of the organization's billing profile.
     * @returns {Promise<Object>} The billing profile information.
     */
    info(organization_id, organization_billing_profile_id) {
        const payload = {
            organization_id,
            organization_billing_profile_id
        }

        return api
            .sentRequest(
                '/api/organization/admin/billing/profile/info',
                payload,
                true
            )
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Creates a new organization billing profile.
     *
     * @param {string} organization_id - The ID of the organization.
     * @param {string} orgbp_name - The name of the organization billing profile.
     * @param {boolean} orgbp_is_enabled - The status of the organization billing profile (enabled or disabled).
     * @returns {Promise<object>} A promise that resolves to the response data or gets a catch response in case of an error.
     */
    create(organization_id, orgbp_name, orgbp_is_enabled) {
        const payload = {
            organization_id,
            orgbp_name,
            orgbp_is_enabled
        }

        return api
            .sentRequest(
                '/api/organization/admin/billing/profile/create',
                payload,
                true
            )
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Updates the organization billing profile with the provided details.
     *
     * @param {string} organization_billing_profile_id - The ID of the organization billing profile to update.
     * @param {string} organization_id - The ID of the organization.
     * @param {string} orgbp_name - The name of the organization billing profile.
     * @param {boolean} orgbp_is_enabled - The status indicating if the organization billing profile is enabled.
     * @returns {Promise<Object>} The response data from the API call.
     */
    update(
        organization_billing_profile_id,
        organization_id,
        orgbp_name,
        orgbp_is_enabled
    ) {
        const payload = {
            organization_billing_profile_id,
            organization_id,
            orgbp_name,
            orgbp_is_enabled
        }

        return api
            .sentRequest(
                '/api/organization/admin/billing/profile/update',
                payload,
                true
            )
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }
}

export const adminOrganizationBillingProfileApi =
    new AdminOrganizationBillingProfileApi()
